import React, { useState, useEffect, useMemo, useRef, useCallback, useContext } from 'react';
import { View, StyleSheet, ScrollView, Text, TouchableOpacity, Image, Platform, Dimensions, FlatList, DeviceEventEmitter, Modal } from 'react-native';
import { MaterialIcons, MaterialCommunityIcons } from '@expo/vector-icons';
import Storage from '@react-native-async-storage/async-storage';
import PlaceHolderProducto from './PlaceHolderProducto';
import Filtros from './Filtros';
import Ordenamiento from './Ordenamiento';
import ScrollToTopButton from './ScrollToTopButton';
import { useProducts } from '../../../../context/ProductContext';
import { UserContext } from '../../../../context/UserContext';
import LoadingScreen from '../../../Common/LoadingScreen';
import { colores, fuentes, layout } from '../../../../styles/variablesCss';
import { BACKEND_URL } from '../../../../Config/Const';
import { useFavoritos } from '../../../../context/FavoritosContext';
import Login from '../../Header/AdminUsuario/Login';

const buildSearchQueryKey = ({ dept = '0', cat = '0', subcat = '0', searchQuery = '', codBanner = '0', ofe = false, suc = '4', mail = '', codUsu = '' }) => {
  return `search:${JSON.stringify({
    dept: String(dept || '0'),
    cat: String(cat || '0'),
    subcat: String(subcat || '0'),
    search: (searchQuery || '').trim(),
    codBanner: String(codBanner || '0'),
    ofe: !!ofe,
    suc: String(suc || '4'),
    mail: String(mail || ''),
    codUsu: String(codUsu || ''),
  })}`;
};

const Productos = ({ navigation, route }) => {
  const { loading: contextLoading, departmentName, setDepartmentName, categoryName, setCategoryName,
    subcategoryName, setSubcategoryName, searchText, setSearchText, departmentId, setDepartmentId,
    categoryId, setCategoryId, subcategoryId, setSubcategoryId, productos: productosContexto,
    setProductos, productosQueryKey, setProductosQueryKey } = useProducts();

  const { codUsu } = useContext(UserContext);
  const { cargarFavoritos } = useFavoritos();

  const cargarMasCompras = async () => {
    const obtenerAuthYSucursal = async () => {
      try {
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
          return {
            mail: localStorage.getItem('mail') || '',
            codUsu: localStorage.getItem('codUsu') || '',
            suc: localStorage.getItem('sucursalId') || '4',
          };
        }
        return {
          mail: (await Storage.getItem('mail')) || '',
          codUsu: (await Storage.getItem('codUsu')) || '',
          suc: (await Storage.getItem('sucursalId')) || '4',
        };
      } catch (e) {
        return { mail: '', codUsu: '', suc: '4' };
      }
    };

    const { mail, codUsu, suc } = await obtenerAuthYSucursal();
    if (!mail || !codUsu) return { productos: [], productosFaltantes: [] };

    const params = new URLSearchParams();
    params.append('mail', mail);
    params.append('codUsu', codUsu);
    params.append('suc', suc);

    const url = `${BACKEND_URL}/rest/productos/mas-compras?${params.toString()}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const text = await response.text();
    let data = null;
    try { data = JSON.parse(text); } catch {}

    const ds = data?.dsResultado || data?.response?.dsResultado?.dsResultado || null;
    const lista = Array.isArray(ds?.productos) ? ds.productos : Array.isArray(ds?.items) ? ds.items : [];
    const faltantes = Array.isArray(ds?.productosFaltantes) ? ds.productosFaltantes : [];
    return { productos: lista, productosFaltantes: faltantes };
  };

  const cargarHistorialDetalle = async (codCompra) => {
    const obtenerAuthYSucursal = async () => {
      try {
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
          return {
            mail: localStorage.getItem('mail') || '',
            codUsu: localStorage.getItem('codUsu') || '',
            suc: localStorage.getItem('sucursalId') || '4',
          };
        }
        return {
          mail: (await Storage.getItem('mail')) || '',
          codUsu: (await Storage.getItem('codUsu')) || '',
          suc: (await Storage.getItem('sucursalId')) || '4',
        };
      } catch (e) {
        return { mail: '', codUsu: '', suc: '4' };
      }
    };

    const cod = codCompra !== undefined && codCompra !== null ? String(codCompra) : '';
    if (!cod) return { productos: [], productosFaltantes: [] };

    const { mail, codUsu, suc } = await obtenerAuthYSucursal();
    if (!mail || !codUsu) return { productos: [], productosFaltantes: [] };

    const params = new URLSearchParams();
    params.append('mail', mail);
    params.append('codUsu', codUsu);
    params.append('suc', suc);
    params.append('codCompra', cod);

    const url = `${BACKEND_URL}/rest/productos/historial/productos?${params.toString()}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const text = await response.text();
    let data = null;
    try { data = JSON.parse(text); } catch {}

    const ds = data?.dsResultado || null;
    const lista = Array.isArray(ds?.productos) ? ds.productos : [];
    const faltantes = Array.isArray(ds?.productosFaltantes) ? ds.productosFaltantes : [];
    return { productos: lista, productosFaltantes: faltantes };
  };

  // Estado de carga local que podemos controlar desde este componente
  const [loading, setLoading] = useState(contextLoading);
  const [filteredProducts, setFilteredProducts] = useState(productosContexto || []);
  const [sortOption, setSortOption] = useState(null);
  const [uiResetKey, setUiResetKey] = useState(0);
  // Estado para rastrear si la busqueda es por banner
  const [isBannerSearch, setIsBannerSearch] = useState(false);
  // Estado para rastrear si la busqueda es por ofertas y combos (ofe=true)
  const [isOfertasSearch, setIsOfertasSearch] = useState(false);
  // Estado para controlar la visibilidad de los filtros en mÃ³vil
  const [showFiltersMobile, setShowFiltersMobile] = useState(false);

  // Estado para controlar la visibilidad del botón de volver arriba
  const [showScrollToTop, setShowScrollToTop] = useState(false);

  // Estado para controlar el modal de "Lo que más comprás"
  const [showMasComprasModal, setShowMasComprasModal] = useState(false);

  // Referencia a la lista para poder hacer scroll programáticamente
  const listRef = useRef(null);

  const [windowWidth, setWindowWidth] = useState(
    Platform.OS === 'web' && typeof window !== 'undefined' ? window.innerWidth : Dimensions.get('window').width
  );
  const isMobile = windowWidth < 768;
  const isSmallWeb = Platform.OS === 'web' && windowWidth < 768;

  // Calcular columnas dinámicamente según el ancho disponible
  const columns = useMemo(() => {
    const filtersWidth = isMobile ? 0 : 240; // coincide con styles.filtersPanel
    const sidePadding = isMobile ? 20 : 30;   // padding aproximado del contenedor/lista
    const gridGap = isMobile ? 8 : 15;       // coincidir con estilos existentes
    const available = Math.max(0, windowWidth - filtersWidth - sidePadding);
    const minCardWidth = isMobile ? 160 : 240; // ancho mínimo razonable de tarjeta
    const maxCols = isMobile ? 3 : 5; // límite superior
    const calc = Math.max(1, Math.floor((available + gridGap) / (minCardWidth + gridGap)));
    return Math.min(maxCols, calc);
  }, [windowWidth, isMobile]);

  // Crear una firma estable de productosContexto para evitar re-renders innecesarios
  const productosContextoSignature = useMemo(() => {
    try {
      if (!productosContexto || !Array.isArray(productosContexto) || productosContexto.length === 0) return '0';
      const first = productosContexto[0]?.plu;
      const last = productosContexto[productosContexto.length - 1]?.plu;
      return `${productosContexto.length}:${String(first)}:${String(last)}`;
    } catch (_) {
      return '0';
    }
  }, [productosContexto]);

  // Función para ordenar productos según la opción seleccionada
  // FunciÃ³n para limpiar estados segÃºn el tipo de busqueda
  const clearSearchStates = (searchType) => {

    if (searchType === 'text') {
      // Si buscamos por texto, limpiamos estados de categorÃ­as
      setDepartmentId('0');
      setCategoryId('0');
      setSubcategoryId('0');
      setDepartmentName('');
      setCategoryName('');
      setSubcategoryName('');
    }
    else if (searchType === 'category') {
      // Si buscamos por categorÃ­a, limpiamos texto de busqueda
      setSearchText('');
    }
    else if (searchType === 'banner') {
      // Si buscamos por banner, limpiamos texto de busqueda y posiblemente categorÃ­as
      // dependiendo de si el banner estÃ¡ asociado a categorÃ­as especÃ­ficas o no
      setSearchText('');
      setDepartmentName('');
      setCategoryName('');
      setSubcategoryName('');
      // Solo limpiamos categorÃ­as si no se especifican en el banner
      if (!departmentId || departmentId === '0') {
        setDepartmentName('');
      }
      if (!categoryId || categoryId === '0') {
        setCategoryName('');
      }
      if (!subcategoryId || subcategoryId === '0') {
        setSubcategoryName('');
      }
    }
  };

  // Calcular el ancho del Ã­tem para responsive (usando windowWidth que ya estÃ¡ definido)
  const isSmallScreen = windowWidth < 768;

  const handleSortChange = (option) => {
    setSortOption(option);
  };

  // Determinar si estamos en web o mÃ³vil
  const isWeb = Platform.OS === 'web';

  // Estado para almacenar productos de la API
  const [productosApi, setProductosApi] = useState([]);

  // Versionador para cambios de sucursal (incrementa para forzar refetch)
  const [sucursalVersion, setSucursalVersion] = useState(0);

  // Estado para reaccionar a cambios en la URL (web) como D, C, S, searchText, codBanner, ofe, etc.
  const [urlSearch, setUrlSearch] = useState(
    isWeb && typeof window !== 'undefined' ? window.location.search : ''
  );

  // Listener de cambios de historial/URL en web para forzar refetch
  useEffect(() => {
    if (!isWeb || typeof window === 'undefined') return;
    const onPopState = () => setUrlSearch(window.location.search);
    const onCustomNavigation = () => setUrlSearch(window.location.search);
    
    window.addEventListener('popstate', onPopState);
    window.addEventListener('navigationChange', onCustomNavigation);
    
    return () => {
      window.removeEventListener('popstate', onPopState);
      window.removeEventListener('navigationChange', onCustomNavigation);
    };
  }, [isWeb]);

  // Listener de scroll para mostrar/ocultar botón de volver arriba en web
  useEffect(() => {
    if (!isWeb || typeof window === 'undefined') return;
    
    const handleWindowScroll = () => {
      const scrollY = window.scrollY || window.pageYOffset;
      
      if (scrollY > 300) {
        setShowScrollToTop(true);
      } else if (scrollY <= 300) {
        setShowScrollToTop(false);
      }
    };
    
    // Ejecutar una vez al montar para verificar posición inicial
    handleWindowScroll();
    
    window.addEventListener('scroll', handleWindowScroll);
    
    return () => {
      window.removeEventListener('scroll', handleWindowScroll);
    };
  }, [isWeb]);

  // Listeners para cambios de sucursal (web y móvil)
  useEffect(() => {
    let removeMobileSubscription;
    // Web: escuchar eventos CustomEvent('localStorageChange') emitidos al cambiar sucursal
    const onLocalStorageChange = (e) => {
      try {
        const key = e?.detail?.key;
        if (key === 'sucursalId') {
          setSucursalVersion((v) => v + 1);
        }
      } catch (_) { }
    };
    if (isWeb && typeof window !== 'undefined') {
      window.addEventListener('localStorageChange', onLocalStorageChange);
    } else {
      // Móvil: DeviceEventEmitter('sucursalChanged')
      removeMobileSubscription = DeviceEventEmitter.addListener('sucursalChanged', () => {
        setSucursalVersion((v) => v + 1);
      });
    }
    return () => {
      if (isWeb && typeof window !== 'undefined') {
        window.removeEventListener('localStorageChange', onLocalStorageChange);
      }
      if (removeMobileSubscription && typeof removeMobileSubscription.remove === 'function') {
        removeMobileSubscription.remove();
      }
    };
  }, [isWeb]);

  // Compartir informaciÃ³n de navegaciÃ³n para que estÃ© disponible en componentes hijos
  useEffect(() => {
    if (isWeb && window) {
      // Crear un objeto con los datos de navegaciÃ³n actuales
      window.navigationContext = {
        searchText,
        departmentName,
        categoryName,
        subcategoryName,
        isBannerSearch
      };
    }
  }, [isWeb, searchText, departmentName, categoryName, subcategoryName, isBannerSearch, sucursalVersion, urlSearch]);

  // Resetear UI (ordenamiento + filtros) cuando cambia la búsqueda/navegación por URL
  useEffect(() => {
    if (!isWeb || typeof window === 'undefined') {
      setSortOption(null);
      setUiResetKey((k) => k + 1);
      return;
    }

    let shouldRestore = false;
    try {
      const raw = sessionStorage.getItem('productosReturnState');
      if (raw) {
        const st = JSON.parse(raw);
        const isFresh = st?.ts ? (Date.now() - Number(st.ts) < 10 * 60 * 1000) : false;
        const matches = (st?.urlSearch || '') === (window.location.search || '');
        if (isFresh && matches) {
          shouldRestore = true;
        }
      }
    } catch (_) {}

    if (!shouldRestore) {
      setSortOption(null);
      setUiResetKey((k) => k + 1);
      // Forzar scroll al tope (ventana + lista). Se hace inmediato y diferido
      // para cubrir tanto el body como el scroller interno del FlatList en web.
      const doScrollTop = () => {
        try {
          if (typeof window !== 'undefined' && window.scrollTo) {
            window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
          }
        } catch (_) {}
        try {
          if (listRef.current && typeof listRef.current.scrollToOffset === 'function') {
            listRef.current.scrollToOffset({ offset: 0, animated: false });
          }
        } catch (_) {}
        // En web con RNW el FlatList suele renderizar con id=nativeID
        try {
          if (typeof document !== 'undefined') {
            const el = document.getElementById('productosFlatList');
            if (el) {
              if (typeof el.scrollTo === 'function') el.scrollTo(0, 0);
              else if (typeof el.scrollTop === 'number') el.scrollTop = 0;
            }
            // También intentar el primer ancestro scrolleable por si acaso
            let p = el && el.parentElement;
            while (p) {
              const style = (typeof window !== 'undefined' && window.getComputedStyle) ? window.getComputedStyle(p) : null;
              const canScroll = style && (style.overflowY === 'auto' || style.overflowY === 'scroll' || style.overflow === 'auto' || style.overflow === 'scroll');
              if (canScroll && typeof p.scrollTop === 'number') {
                p.scrollTop = 0;
                break;
              }
              p = p.parentElement;
            }
          }
        } catch (_) {}
      };
      doScrollTop();
      // Múltiples ticks para vencer timing de layout / render de lista
      if (typeof requestAnimationFrame !== 'undefined') {
        requestAnimationFrame(() => {
          doScrollTop();
          setTimeout(doScrollTop, 0);
        });
      } else {
        setTimeout(doScrollTop, 0);
        setTimeout(doScrollTop, 16);
      }
    }
  }, [urlSearch]);

  // Restaurar scroll al volver desde el detalle (solo si coincide la misma búsqueda)
  useEffect(() => {
    if (!isWeb || typeof window === 'undefined') return;

    try {
      const raw = sessionStorage.getItem('productosReturnState');
      if (!raw) return;
      const st = JSON.parse(raw);

      const isFresh = st?.ts ? (Date.now() - Number(st.ts) < 10 * 60 * 1000) : false;
      const matches = (st?.urlSearch || '') === (window.location.search || '');
      if (!isFresh || !matches) return;

      const y = Number(st?.scrollY || 0);
      // Esperar un tick para que el DOM y el layout estén listos
      setTimeout(() => {
        try {
          window.scrollTo({ top: y, left: 0, behavior: 'auto' });
        } catch (_) {}
      }, 0);

      sessionStorage.removeItem('productosReturnState');
    } catch (_) {}
  }, [isWeb, urlSearch]);

  // Sincronizar estado de loading con el contexto (separado para evitar loops)
  useEffect(() => {
    setLoading(contextLoading);
  }, [contextLoading]);

  // Detectar si se accede con masCompras=true y mostrar modal o login
  useEffect(() => {
    if (!isWeb || typeof window === 'undefined') return;
    
    const params = new URLSearchParams(window.location.search);
    if (params.get('masCompras') === 'true') {
      // Si no está logeado, siempre mostrar login
      if (!codUsu) {
        setShowMasComprasModal(true);
      } else {
        // Si está logeado, verificar si el usuario ya marcó "No volver a mostrar"
        const modalShown = localStorage.getItem('masComprasModalShown');
        if (modalShown) {
          // Ya fue mostrado, no mostrar el modal
          setShowMasComprasModal(false);
        } else {
          // No fue mostrado aún, mostrar el modal
          setShowMasComprasModal(true);
        }
      }
    } else {
      // Si no estamos en masCompras, resetear el estado
      setShowMasComprasModal(false);
    }
  }, [isWeb, urlSearch, codUsu]);

  // Efecto para buscar productos usando parámetros de busqueda según la plataforma
  useEffect(() => {
    const fetchData = async () => {
      // Cache rápido en web: evita refetch y evita mostrar loading al volver atrás
      if (isWeb && typeof window !== 'undefined') {
        try {
          const suc = localStorage.getItem('sucursalId') || '4';
          const mail = localStorage.getItem('mail') || '';
          const codUsu = localStorage.getItem('codUsu') || '';
          const cacheKey = `productosCache:v1:${window.location.search || ''}|suc:${suc}|mail:${mail}|codUsu:${codUsu}`;
          const cached = sessionStorage.getItem(cacheKey);
          if (cached) {
            const parsed = JSON.parse(cached);
            const lista = Array.isArray(parsed?.productos) ? parsed.productos : [];
            setProductosApi(lista);
            setFilteredProducts(lista);
            setProductos(lista);
            setLoading(false);
            return;
          }
        } catch (_) {}
      }

      // Activamos el estado de carga al iniciar la busqueda
      setLoading(true);
      try {
        // Valores para los parÃ¡metros de busqueda
        let dept = '';
        let cat = '';
        let subcat = '';
        let searchQuery = '';
        let codBanner = '0'; // Inicializar codBanner con valor por defecto 0
        let ofe = false; // flag ofertas (oyc-rdw)

        // Si estamos en web, obtener valores de la URL
        if (isWeb && window && window.location) {
          const params = new URLSearchParams(window.location.search);

          // Verificar si es historial, mÃ¡s comprados o favoritos
          const isHistorial = params.get('historial') === 'true';
          const isMasCompras = params.get('masCompras') === 'true';
          const isFavoritos = params.get('favoritos') === 'true';
          const codCompraParam = params.get('codCompra') || '';

          if ((isHistorial || isMasCompras || isFavoritos) && productosContexto && productosContexto.length > 0) {
            const tipo = isHistorial ? 'historial' : isMasCompras ? 'mÃ¡s comprados' : 'favoritos';
            setProductosApi(productosContexto);
            setFilteredProducts(productosContexto);
            setLoading(false);
            return;
          }

          // Caso especial: reload directo en /productos?historial=true&codCompra=...
          // Si el contexto viene vacío, buscar el detalle del historial desde la API /historial/productos.
          if (isHistorial && codCompraParam && (!productosContexto || productosContexto.length === 0)) {
            try {
              const res = await cargarHistorialDetalle(codCompraParam);
              const disponibles = Array.isArray(res?.productos) ? res.productos : [];
              const faltantes = Array.isArray(res?.productosFaltantes)
                ? res.productosFaltantes.map((p) => ({
                    ...p,
                    faltante: true,
                    precio: p.precio ?? 0,
                    precioSinImp: p.precioSinImp ?? 0,
                    stock: p.stock ?? 0,
                    maxStock: p.maxStock ?? 0,
                    minStock: p.minStock ?? 1,
                    oferta: p.oferta ?? '',
                    cuotasSinInt: p.cuotasSinInt ?? 0,
                    fotos: p.fotos ?? (p.foto ? [{ plu: p.plu, imagen: p.foto }] : []),
                  }))
                : [];
              const combined = [...disponibles, ...faltantes];
              setProductosApi(combined);
              setFilteredProducts(combined);
              setLoading(false);
              return;
            } catch (e) {
              console.error('Error al cargar historial en reload:', e);
              setProductosApi([]);
              setFilteredProducts([]);
              setLoading(false);
              return;
            }
          }

          // Caso especial: reload directo en /productos?masCompras=true
          // Si el contexto viene vacío, buscar más compras desde la API /mas-compras.
          if (isMasCompras && (!productosContexto || productosContexto.length === 0)) {
            try {
              const res = await cargarMasCompras();
              const disponibles = Array.isArray(res?.productos) ? res.productos : [];
              const faltantes = Array.isArray(res?.productosFaltantes)
                ? res.productosFaltantes.map((p) => ({
                    ...p,
                    faltante: true,
                    precio: p.precio ?? 0,
                    precioSinImp: p.precioSinImp ?? 0,
                    stock: p.stock ?? 0,
                    maxStock: p.maxStock ?? 0,
                    minStock: p.minStock ?? 1,
                    oferta: p.oferta ?? '',
                    cuotasSinInt: p.cuotasSinInt ?? 0,
                    fotos: p.fotos ?? (p.foto ? [{ plu: p.plu, imagen: p.foto }] : []),
                  }))
                : [];
              const combined = [...disponibles, ...faltantes];
              setProductosApi(combined);
              setFilteredProducts(combined);
              setLoading(false);
              return;
            } catch (e) {
              console.error('Error al cargar más compras en reload:', e);
              setProductosApi([]);
              setFilteredProducts([]);
              setLoading(false);
              return;
            }
          }

          // Caso especial: reload directo en /productos?favoritos=true
          // Si el contexto viene vacío, buscar favoritos desde la API /favoritos.
          if (isFavoritos && (!productosContexto || productosContexto.length === 0)) {
            try {
              const res = await cargarFavoritos();
              const disponibles = Array.isArray(res?.productos) ? res.productos : [];
              const faltantes = Array.isArray(res?.productosFaltantes)
                ? res.productosFaltantes.map((p) => ({
                    ...p,
                    faltante: true,
                    precio: p.precio ?? 0,
                    precioSinImp: p.precioSinImp ?? 0,
                    stock: p.stock ?? 0,
                    maxStock: p.maxStock ?? 0,
                    minStock: p.minStock ?? 1,
                    oferta: p.oferta ?? '',
                    cuotasSinInt: p.cuotasSinInt ?? 0,
                    fotos: p.fotos ?? (p.foto ? [{ plu: p.plu, imagen: p.foto }] : []),
                  }))
                : [];
              const combined = [...disponibles, ...faltantes];
              setProductosApi(combined);
              setFilteredProducts(combined);
              setLoading(false);
              return;
            } catch (e) {
              console.error('Error al cargar favoritos en reload:', e);
              setProductosApi([]);
              setFilteredProducts([]);
              setLoading(false);
              return;
            }
          }

          dept = params.get('D') || '';
          cat = params.get('C') || '';
          subcat = params.get('S') || '';
          searchQuery = params.get('searchText') || '';
          // Obtener nombres de categorías de la URL
          const urlDeptName = params.get('deptName') || '';
          const urlCatName = params.get('catName') || '';
          const urlSubcatName = params.get('subcatName') || '';
          // Obtener codBanner de la URL, si existe
          codBanner = params.get('codBanner') || '0';
          // Obtener flag 'ofe' desde la URL si existe (true/false)
          const ofeParam = params.get('ofe');
          if (typeof ofeParam === 'string') {
            ofe = (ofeParam === 'true');
          }

          // Determinar si la busqueda es por banner
          const isBanner = codBanner !== '0';
          setIsBannerSearch(isBanner);
          
          // Determinar si la busqueda es por ofertas y combos (ofe=true)
          setIsOfertasSearch(ofe === true);

          // No sincronizar estados aquí para evitar bucle infinito
          // Los estados ya se sincronizan en otros useEffect específicos
        }
        // Si estamos en mÃ³vil
        else {
          // Verificar si es historial, mÃ¡s comprados o favoritos desde route params
          const isHistorial = route?.params?.historial === true;
          const isMasCompras = route?.params?.masCompras === true;
          const isFavoritos = route?.params?.favoritos === true;
          const codCompraParam = route?.params?.codCompra;
          // Leer 'ofe' desde params en móvil si viene seteado
          if (route?.params && typeof route.params.ofe !== 'undefined') {
            ofe = !!route.params.ofe;
          }
          
          // Determinar si la busqueda es por ofertas y combos (ofe=true) en móvil
          setIsOfertasSearch(ofe === true);

          if ((isHistorial || isMasCompras || isFavoritos) && productosContexto && productosContexto.length > 0) {
            const tipo = isHistorial ? 'historial' : isMasCompras ? 'mÃ¡s comprados' : 'favoritos';
            setProductosApi(productosContexto);
            setFilteredProducts(productosContexto);
            setLoading(false);
            return;
          }

          // Caso especial móvil: detalle historial con codCompra, pero el contexto viene vacío.
          if (isHistorial && codCompraParam && (!productosContexto || productosContexto.length === 0)) {
            try {
              const res = await cargarHistorialDetalle(codCompraParam);
              const disponibles = Array.isArray(res?.productos) ? res.productos : [];
              const faltantes = Array.isArray(res?.productosFaltantes)
                ? res.productosFaltantes.map((p) => ({
                    ...p,
                    faltante: true,
                    precio: p.precio ?? 0,
                    precioSinImp: p.precioSinImp ?? 0,
                    stock: p.stock ?? 0,
                    maxStock: p.maxStock ?? 0,
                    minStock: p.minStock ?? 1,
                    oferta: p.oferta ?? '',
                    cuotasSinInt: p.cuotasSinInt ?? 0,
                    fotos: p.fotos ?? (p.foto ? [{ plu: p.plu, imagen: p.foto }] : []),
                  }))
                : [];
              const combined = [...disponibles, ...faltantes];
              setProductosApi(combined);
              setFilteredProducts(combined);
              setLoading(false);
              return;
            } catch (e) {
              console.error('Error al cargar historial en móvil:', e);
              setProductosApi([]);
              setFilteredProducts([]);
              setLoading(false);
              return;
            }
          }

          // Caso especial móvil: si masCompras=true pero el contexto viene vacío, cargar desde API.
          if (isMasCompras && (!productosContexto || productosContexto.length === 0)) {
            try {
              const res = await cargarMasCompras();
              const disponibles = Array.isArray(res?.productos) ? res.productos : [];
              const faltantes = Array.isArray(res?.productosFaltantes)
                ? res.productosFaltantes.map((p) => ({
                    ...p,
                    faltante: true,
                    precio: p.precio ?? 0,
                    precioSinImp: p.precioSinImp ?? 0,
                    stock: p.stock ?? 0,
                    maxStock: p.maxStock ?? 0,
                    minStock: p.minStock ?? 1,
                    oferta: p.oferta ?? '',
                    cuotasSinInt: p.cuotasSinInt ?? 0,
                    fotos: p.fotos ?? (p.foto ? [{ plu: p.plu, imagen: p.foto }] : []),
                  }))
                : [];
              const combined = [...disponibles, ...faltantes];
              setProductosApi(combined);
              setFilteredProducts(combined);
              setLoading(false);
              return;
            } catch (e) {
              console.error('Error al cargar más compras en móvil:', e);
              setProductosApi([]);
              setFilteredProducts([]);
              setLoading(false);
              return;
            }
          }

          // Caso especial móvil: si favoritos=true pero el contexto viene vacío, cargar desde API.
          if (isFavoritos && (!productosContexto || productosContexto.length === 0)) {
            try {
              const res = await cargarFavoritos();
              const disponibles = Array.isArray(res?.productos) ? res.productos : [];
              const faltantes = Array.isArray(res?.productosFaltantes)
                ? res.productosFaltantes.map((p) => ({
                    ...p,
                    faltante: true,
                    precio: p.precio ?? 0,
                    precioSinImp: p.precioSinImp ?? 0,
                    stock: p.stock ?? 0,
                    maxStock: p.maxStock ?? 0,
                    minStock: p.minStock ?? 1,
                    oferta: p.oferta ?? '',
                    cuotasSinInt: p.cuotasSinInt ?? 0,
                    fotos: p.fotos ?? (p.foto ? [{ plu: p.plu, imagen: p.foto }] : []),
                  }))
                : [];
              const combined = [...disponibles, ...faltantes];
              setProductosApi(combined);
              setFilteredProducts(combined);
              setLoading(false);
              return;
            } catch (e) {
              console.error('Error al cargar favoritos en móvil:', e);
              setProductosApi([]);
              setFilteredProducts([]);
              setLoading(false);
              return;
            }
          }

          searchQuery = searchText || '';

          // Si hay texto de busqueda, poner departamento/categorÃ­a/subcategorÃ­a en cero
          if (searchText && searchText.trim() !== '') {
            dept = '0';
            cat = '0';
            subcat = '0';
          } else {
            // Si no hay busqueda por texto, usar los IDs del contexto
            dept = departmentId || '0';
            cat = categoryId || '0';
            subcat = subcategoryId || '0';
          }

          // Para mÃ³vil siempre usar codBanner en 0
          codBanner = '0';
        }

        // Obtener mail y codUsu del almacenamiento (igual que en HeaderRojo)
        const mail = await Storage.getItem('mail') || '';
        const codUsu = await Storage.getItem('codUsu') || '';
        const suc = await Storage.getItem('sucursalId') || '';

        const normalizedDept = String(dept || '0');
        const normalizedCat = String(cat || '0');
        const normalizedSubcat = String(subcat || '0');
        const normalizedSearchQuery = (searchQuery || '').trim();
        const normalizedCodBanner = String(codBanner || '0');
        const normalizedSuc = String((suc && String(suc).trim() !== '') ? suc : '4');
        const normalizedMail = String(mail || '');
        const normalizedCodUsu = String(codUsu || '');

        const nextQueryKey = buildSearchQueryKey({
          dept: normalizedDept,
          cat: normalizedCat,
          subcat: normalizedSubcat,
          searchQuery: normalizedSearchQuery,
          codBanner: normalizedCodBanner,
          ofe,
          suc: normalizedSuc,
          mail: normalizedMail,
          codUsu: normalizedCodUsu,
        });

        if (
          productosQueryKey &&
          productosQueryKey === nextQueryKey &&
          Array.isArray(productosContexto)
        ) {
          setProductosApi(productosContexto);
          setFilteredProducts(productosContexto);
          setLoading(false);

          // Cache hit: aún así forzar scroll al tope en web al cambiar de búsqueda/categoría
          if (isWeb && typeof window !== 'undefined') {
            let shouldRestore = false;
            try {
              const raw = sessionStorage.getItem('productosReturnState');
              if (raw) {
                const st = JSON.parse(raw);
                const isFresh = st?.ts ? (Date.now() - Number(st.ts) < 10 * 60 * 1000) : false;
                const matches = (st?.urlSearch || '') === (window.location.search || '');
                if (isFresh && matches) shouldRestore = true;
              }
            } catch (_) {}
            if (!shouldRestore) {
              const doTop = () => {
                try { window.scrollTo({ top: 0, left: 0, behavior: 'auto' }); } catch (_) {}
                try { if (listRef.current && typeof listRef.current.scrollToOffset === 'function') listRef.current.scrollToOffset({ offset: 0, animated: false }); } catch (_) {}
                try {
                  if (typeof document !== 'undefined') {
                    const de = document.documentElement; if (de) de.scrollTop = 0;
                    const bd = document.body; if (bd) bd.scrollTop = 0;
                    const el = document.getElementById('productosFlatList');
                    if (el) { if (typeof el.scrollTo === 'function') el.scrollTo(0, 0); else if (typeof el.scrollTop === 'number') el.scrollTop = 0; }
                  }
                } catch (_) {}
              };
              doTop();
              if (typeof requestAnimationFrame !== 'undefined') {
                requestAnimationFrame(() => { doTop(); setTimeout(doTop, 0); });
              } else {
                setTimeout(doTop, 0);
                setTimeout(doTop, 16);
              }
            }
          }
          return;
        }

        // Crear objeto de parÃ¡metros solo con valores significativos
        const params = {};

        // ParÃ¡metro de sucursal siempre es necesario
        params.suc = normalizedSuc;

        // ParÃ¡metros de categorÃ­as solo si tienen valores
        if (normalizedDept !== '0') params.codDep = normalizedDept;
        if (normalizedCat !== '0') params.codCat = normalizedCat;
        if (normalizedSubcat !== '0') params.codSubCat = normalizedSubcat;

        // ParÃ¡metro de busqueda solo si tiene valor
        if (normalizedSearchQuery !== '') params.producto = normalizedSearchQuery;

        // Banner solo si es diferente de 0 o vacÃ­o
        if (normalizedCodBanner !== '0') params.codBanner = normalizedCodBanner;

        // Flag de ofertas (oyc-rdw) solo si es true (enviar como 'true')
        if (ofe === true) params.ofe = 'true';

        // ParÃ¡metros de usuario solo si tienen valores
        if (normalizedMail.trim() !== '') params.mail = normalizedMail;
        if (normalizedCodUsu.trim() !== '') params.codUsu = normalizedCodUsu;

        // Crear URLSearchParams a partir del objeto filtrado
        const queryParams = new URLSearchParams(params);
        const response = await fetch(`${BACKEND_URL}/rest/productos/productos?${queryParams.toString()}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });

        const data = await response.json();

        if (data.dsResultado && data.dsResultado.productos) {
          // Mostrar muestra del primer producto si existe
          if (data.dsResultado.productos.length > 0) {
          }

          setProductosApi(data.dsResultado.productos);
          setFilteredProducts(data.dsResultado.productos);
          setProductos(data.dsResultado.productos);
          setProductosQueryKey(nextQueryKey);

          // En web, al cargar una nueva búsqueda/categoría, forzar scroll al tope
          // (después de que llegan los datos para cubrir cambios de altura del contenido).
          if (isWeb && typeof window !== 'undefined') {
            let shouldRestore = false;
            try {
              const raw = sessionStorage.getItem('productosReturnState');
              if (raw) {
                const st = JSON.parse(raw);
                const isFresh = st?.ts ? (Date.now() - Number(st.ts) < 10 * 60 * 1000) : false;
                const matches = (st?.urlSearch || '') === (window.location.search || '');
                if (isFresh && matches) shouldRestore = true;
              }
            } catch (_) {}
            if (!shouldRestore) {
              const doTop = () => {
                try { window.scrollTo({ top: 0, left: 0, behavior: 'auto' }); } catch (_) {}
                try { if (listRef.current && typeof listRef.current.scrollToOffset === 'function') listRef.current.scrollToOffset({ offset: 0, animated: false }); } catch (_) {}
                try {
                  const el = document.getElementById('productosFlatList');
                  if (el) { if (typeof el.scrollTo === 'function') el.scrollTo(0, 0); else if (typeof el.scrollTop === 'number') el.scrollTop = 0; }
                } catch (_) {}
              };
              doTop();
              if (typeof requestAnimationFrame !== 'undefined') {
                requestAnimationFrame(() => { doTop(); setTimeout(doTop, 0); });
              } else {
                setTimeout(doTop, 0);
                setTimeout(doTop, 16);
              }
            }
          }

          // Guardar cache en web
          if (isWeb && typeof window !== 'undefined') {
            try {
              const suc = localStorage.getItem('sucursalId') || '4';
              const mail = localStorage.getItem('mail') || '';
              const codUsu = localStorage.getItem('codUsu') || '';
              const cacheKey = `productosCache:v1:${window.location.search || ''}|suc:${suc}|mail:${mail}|codUsu:${codUsu}`;
              sessionStorage.setItem(cacheKey, JSON.stringify({ productos: data.dsResultado.productos, ts: Date.now() }));
            } catch (_) {}
          }
        } else {
          setProductosApi([]);
          setFilteredProducts([]);
          setProductos([]);
          setProductosQueryKey(nextQueryKey);

          if (isWeb && typeof window !== 'undefined') {
            try {
              const suc = localStorage.getItem('sucursalId') || '4';
              const mail = localStorage.getItem('mail') || '';
              const codUsu = localStorage.getItem('codUsu') || '';
              const cacheKey = `productosCache:v1:${window.location.search || ''}|suc:${suc}|mail:${mail}|codUsu:${codUsu}`;
              sessionStorage.setItem(cacheKey, JSON.stringify({ productos: [], ts: Date.now() }));
            } catch (_) {}
          }
        }
      } catch (err) {
        console.error('Error al buscar productos:', err);
        setProductosApi([]);
        setFilteredProducts([]);
        setProductos([]);
        setProductosQueryKey(null);
      } finally {
        // Desactivamos el estado de carga al finalizar, sea exitoso o no
        setLoading(false);
      }
    };

    fetchData();
  }, [ 
    isWeb,
    urlSearch, // refetch cuando cambia la URL en web
    sucursalVersion, // refetch cuando cambia la sucursal
    departmentId,
    categoryId,
    subcategoryId,
    searchText,
    productosContextoSignature, // usar firma estable en lugar del array completo
    route?.params?.historial,
    route?.params?.masCompras,
    route?.params?.favoritos,
  ]);

  // Listener para cambios de tamaÃ±o de ventana
  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const handleResize = () => {
        setWindowWidth(window.innerWidth);
      };

      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  // Extraer categorÃ­as Ãºnicas de los productos
  const categorias = productosApi ? [...new Set(productosApi.map(p => p.tipo))].filter(Boolean) : [];

  // Extraer marcas Ãºnicas de los productos
  const marcas = useMemo(() => {
    if (!productosApi) return [];
    return [...new Set(productosApi.map(p => p.marca))].filter(Boolean).sort();
  }, [productosApi]);
  // Estado para almacenar la navegaciÃ³n por categorÃ­as (para enlaces cliqueables)
  const [pathSegments, setPathSegments] = useState({
    dept: { id: null, name: '' },
    cat: { id: null, name: '' },
    subcat: { id: null, name: '' }
  });

  // Estado para almacenar la ruta de navegaciÃ³n visual
  const [categoryPath, setCategoryPath] = useState('');


  // Estado para almacenar categorÃ­as y subcategorÃ­as
  const [categories, setCategories] = useState([]);
  // Estado para guardar las subcategorÃ­as separadamente
  const [subcategories, setSubcategories] = useState([]);

  // FunciÃ³n para construir la ruta completa de categorÃ­as segÃºn la nueva implementaciÃ³n
  // Esta funciÃ³n serÃ¡ implementada con la nueva lÃ³gica de manejo de rutas y parÃ¡metros
  const buildFullCategoryPath = (allCategories, allSubcategories, params = {}) => {

    // Por ahora, devolvemos valores por defecto
    return {
      path: '',
      segments: {
        dept: { id: null, name: '' },
        cat: { id: null, name: '' },
        subcat: { id: null, name: '' }
      }
    };

  };

  // Efecto para obtener todas las categorÃ­as y subcategorÃ­as desde el backend al montar el componente
  useEffect(() => {
    // Obtener categorÃ­as y subcategorÃ­as del backend
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/rest/productos/categorias`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();

          if (data.dsResultado) {
            // Guardar todas las categorÃ­as y subcategorÃ­as
            if (data.dsResultado.categorias) {
              setCategories(data.dsResultado.categorias);
            }

            if (data.dsResultado.subcategorias) {
              setSubcategories(data.dsResultado.subcategorias);
            }

            // Una vez que tenemos todos los datos, construir la ruta completa
            const result = buildFullCategoryPath(data.dsResultado.categorias || [], data.dsResultado.subcategorias || []);
            if (result && result.path) {
              setCategoryPath(result.path);
            }
            if (result && result.segments) {
              setPathSegments(result.segments);
            }
          }
        }
      } catch (error) {
        console.error('Error al obtener categorÃ­as:', error);
      }
    };

    fetchCategories();
  }, []);

  // Efecto para actualizar la ruta de categorÃ­as cuando cambien las categorÃ­as o subcategorÃ­as
  useEffect(() => {
    // Solo construir el path si tenemos datos suficientes
    if (categories.length > 0 || subcategories.length > 0) {
      const result = buildFullCategoryPath(categories, subcategories);
      if (result && result.path) {
        setCategoryPath(result.path);
      }
      if (result && result.segments) {
        setPathSegments(result.segments);
      }
    }
  }, [categories, subcategories]);

  // FunciÃ³n para filtrar productos por categorÃ­a
  const filterProductsByCategory = (deptId, catId, subcatId) => {
    if (!filteredProducts || !filteredProducts.length) return [];

    return filteredProducts.filter(producto => {
      // Verificar departamento (chequear mÃºltiples variaciones de nombres de propiedad)
      const productDeptMatches = deptId ? (
        (producto.coddepto && parseInt(producto.coddepto) === parseInt(deptId)) ||
        (producto.departamentoId && parseInt(producto.departamentoId) === parseInt(deptId)) ||
        (producto.deptId && parseInt(producto.deptId) === parseInt(deptId))
      ) : true;

      // Verificar categorÃ­a
      const productCatMatches = catId ? (
        (producto.codcat && parseInt(producto.codcat) === parseInt(catId)) ||
        (producto.categoriaId && parseInt(producto.categoriaId) === parseInt(catId)) ||
        (producto.catId && parseInt(producto.catId) === parseInt(catId))
      ) : true;

      // Verificar subcategorÃ­a
      const productSubcatMatches = subcatId ? (
        (producto.codsubcat && parseInt(producto.codsubcat) === parseInt(subcatId)) ||
        (producto.subcategoriaId && parseInt(producto.subcategoriaId) === parseInt(subcatId)) ||
        (producto.subcatId && parseInt(producto.subcatId) === parseInt(subcatId))
      ) : true;

      // El producto debe cumplir con todos los filtros aplicados
      return productDeptMatches && productCatMatches && productSubcatMatches;
    });
  };

  // FunciÃ³n para navegar a un segmento especÃ­fico de la ruta
  const navigateToPathSegment = (segmentType) => {
    // Definir los parÃ¡metros segÃºn el segmento seleccionado
    let params = {};

    // Al navegar por categorÃ­a, siempre limpiar el contexto de busqueda
    clearSearchStates('category');

    if (segmentType === 'dept') {
      params = {
        D: pathSegments.dept.id,
        deptName: pathSegments.dept.name
      };
    } else if (segmentType === 'cat') {
      params = {
        D: pathSegments.dept.id,
        C: pathSegments.cat.id,
        deptName: pathSegments.dept.name,
        catName: pathSegments.cat.name
      };
    }

    // Priorizar React Router DOM para URLs web-friendly
    if (Platform.OS === 'web' && window) {
      // React Router DOM - construir URL amigable
      let url = '/productos';
      if (params.D) {
        url += `/${params.D}`;
        if (params.C) url += `/${params.C}`;
        // No aÃ±adimos subcategorÃ­a aquÃ­ porque estamos navegando a dept o cat
      }

      // Crear objeto URLSearchParams para los parÃ¡metros adicionales
      const searchParams = new URLSearchParams();
      if (params.deptName) searchParams.set('deptName', params.deptName);
      if (params.catName) searchParams.set('catName', params.catName);
      // AÃ±adir el parÃ¡metro codBanner con valor 0
      searchParams.set('codBanner', '0');

      // Construir URL completa con parÃ¡metros de busqueda
      const fullUrl = searchParams.toString() ? `${url}?${searchParams.toString()}` : url;

      // Usar history.pushState para cambiar la URL sin recargar la pÃ¡gina
      window.history.pushState({}, '', fullUrl);

      // Disparar un evento para que los componentes que escuchan cambios de URL puedan reaccionar
      window.dispatchEvent(new Event('navigationChange'));
    }
    // Fallback a React Navigation para entornos mÃ³viles
    else if (navigation && navigation.navigate) {
      navigation.navigate('Productos', params);
    }
  };

  // Función para renderizar la ruta de categorías como enlaces cliqueables (breadcrumbs)
  const renderCategoryPathLinks = () => {
    // Usar los valores del contexto directamente (departmentName, categoryName, subcategoryName)
    // que ya vienen de la URL parseada
    if (!departmentName && !categoryName && !subcategoryName) {
      return null;
    }

    // Función para navegar al departamento
    const handleDeptClick = () => {
      clearSearchStates('category');

      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        const searchParams = new URLSearchParams();
        searchParams.set('D', departmentId || '0');
        searchParams.set('C', '0'); // Limpiar categoría
        searchParams.set('S', '0'); // Limpiar subcategoría
        searchParams.set('deptName', departmentName || '');
        searchParams.set('catName', ''); // Limpiar nombre categoría
        searchParams.set('subcatName', ''); // Limpiar nombre subcategoría
        searchParams.set('codBanner', '0');

        const url = `/productos?${searchParams.toString()}`;
        window.history.pushState({}, '', url);
        window.dispatchEvent(new Event('navigationChange'));
      } else if (navigation) {
        navigation.navigate('Productos', {
          D: departmentId,
          C: 0,
          S: 0,
          deptName: departmentName,
          catName: '',
          subcatName: ''
        });
      }
    };

    // Función para navegar a la categoría
    const handleCatClick = () => {
      clearSearchStates('category');

      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        const searchParams = new URLSearchParams();
        searchParams.set('D', departmentId || '0');
        searchParams.set('C', categoryId || '0');
        searchParams.set('S', '0'); // Limpiar subcategoría
        searchParams.set('deptName', departmentName || '');
        searchParams.set('catName', categoryName || '');
        searchParams.set('subcatName', ''); // Limpiar nombre subcategoría
        searchParams.set('codBanner', '0');

        const url = `/productos?${searchParams.toString()}`;
        window.history.pushState({}, '', url);
        window.dispatchEvent(new Event('navigationChange'));
      } else if (navigation) {
        navigation.navigate('Productos', {
          D: departmentId,
          C: categoryId,
          S: 0,
          deptName: departmentName,
          catName: categoryName,
          subcatName: ''
        });
      }
    };

    // Construir el render de la ruta con segmentos cliqueables
    return (
      <View style={styles.categoryPathContainer}>
        {/* Departamento - cliqueable si hay categoría o subcategoría */}
        {departmentName && (
          (categoryName || subcategoryName) ? (
            <TouchableOpacity onPress={handleDeptClick}>
              <Text style={styles.categoryLinkText}>{departmentName.toUpperCase()}</Text>
            </TouchableOpacity>
          ) : (
            <Text style={styles.categoryTextBold}>{departmentName.toUpperCase()}</Text>
          )
        )}

        {/* Separador | */}
        {departmentName && categoryName && <Text style={styles.categoryPathSeparator}> | </Text>}

        {/* Categoría - cliqueable si hay subcategoría seleccionada */}
        {categoryName && (
          subcategoryName ? (
            <TouchableOpacity onPress={handleCatClick}>
              <Text style={styles.categoryLinkText}>{categoryName.toUpperCase()}</Text>
            </TouchableOpacity>
          ) : (
            <Text style={styles.categoryTextBold}>{categoryName.toUpperCase()}</Text>
          )
        )}

        {/* Separador | */}
        {categoryName && subcategoryName && <Text style={styles.categoryPathSeparator}> | </Text>}

        {/* Subcategoría - nunca cliqueable, es el nivel más profundo */}
        {subcategoryName && (
          <Text style={styles.categoryTextBold}>{subcategoryName.toUpperCase()}</Text>
        )}
      </View>
    );
  };

  // Manejar eventos de scroll para mostrar/ocultar el botÃ³n de volver arriba
  const handleScroll = (event) => {
    // Obtener la posiciÃ³n del scroll
    const scrollY = event.nativeEvent.contentOffset.y;

    // Mostrar el botÃ³n cuando scrolleamos mÃ¡s de 300 pÃ­xeles hacia abajo
    if (scrollY > 300 && !showScrollToTop) {
      setShowScrollToTop(true);
    } else if (scrollY <= 300 && showScrollToTop) {
      setShowScrollToTop(false);
    }
  };

  // Función para hacer scroll hacia arriba cuando se presiona el botón
  const scrollToTop = () => {
    if (isWeb && typeof window !== 'undefined') {
      // En web, usar window.scrollTo con comportamiento suave
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (listRef.current && listRef.current.scrollToOffset) {
      // En móvil, usar el método del FlatList
      listRef.current.scrollToOffset({ offset: 0, animated: true });
    }
  };

  // Función que recibe los productos filtrados del componente Filtros
  const handleFiltersChanged = useCallback((productosFiltrados) => {
    // Guardar el resultado de filtros. El ordenamiento se aplica globalmente al renderizar.
    setFilteredProducts(productosFiltrados);
  }, []);

  // Función para aplicar el ordenamiento según la opción seleccionada
  // isOfertas: true cuando es búsqueda de ofertas y combos (ofe=true)
  const applySorting = (sortedProducts, sortType, isOfertas = false) => {
    try {
      if (!Array.isArray(sortedProducts) || sortedProducts.length === 0) {
        return [];
      }

      // Separar productos disponibles y faltantes
      const disponibles = sortedProducts.filter(p => !p.faltante);
      const faltantes = sortedProducts.filter(p => p.faltante);

      const parsePrecioNumerico = (s) => {
        if (!s) return Infinity;
        const normalized = String(s)
          .replace(/[^0-9,.-]/g, '')
          .replace(/\.(?=\d{3}(\D|$))/g, '')
          .replace(',', '.');
        const n = parseFloat(normalized);
        return Number.isFinite(n) ? n : Infinity;
      };

      const getPrecioFinal = (producto) => {
        if (!producto) return Infinity;

        const comboTieneDatos = Array.isArray(producto.combo) && producto.combo.length > 0 && producto.combo[0];
        if (comboTieneDatos) {
          const totalCantObligCombo = producto.combo.reduce((sum, c) => sum + (c?.cantOblig || 0), 0);
          const usarPrecio = producto.combo[0]?.tipoCombo === 4 || totalCantObligCombo > 0;

          if (usarPrecio) {
            return parsePrecioNumerico(producto.precio);
          }

          return parsePrecioNumerico(producto.combo[0]?.precom);
        }

        const precioBase = parsePrecioNumerico(producto.precio);
        const mult = parsePrecioNumerico(producto.minStock);

        if (Number.isFinite(precioBase) && Number.isFinite(mult)) {
          const final = precioBase * mult;
          return Number.isFinite(final) ? final : Infinity;
        }

        return precioBase;
      };

      let resultado = [...disponibles];

      // Ordenamiento especial para ofertas y combos (ofe=true)
      // Prioridad: 1° campo orden (mayor a menor), 2° codDepto, 3° codcat, 4° subCategoria
      if (isOfertas && (!sortType || sortType === 'relevancia')) {
        resultado.sort((a, b) => {
          // 1° Ordenar por campo 'orden' de mayor a menor
          const ordenA = typeof a?.orden === 'number' ? a.orden : 0;
          const ordenB = typeof b?.orden === 'number' ? b.orden : 0;
          if (ordenB !== ordenA) return ordenB - ordenA;

          // 2° Ordenar por departamento (codDepto)
          const deptA = Number(a?.codDepto) || 0;
          const deptB = Number(b?.codDepto) || 0;
          if (deptA !== deptB) return deptA - deptB;

          // 3° Ordenar por categoría (codcat)
          const catA = Number(a?.codcat) || 0;
          const catB = Number(b?.codcat) || 0;
          if (catA !== catB) return catA - catB;

          // 4° Ordenar por subcategoría (subCategoria)
          const subcatA = Number(a?.subCategoria) || 0;
          const subcatB = Number(b?.subCategoria) || 0;
          if (subcatA !== subcatB) return subcatA - subcatB;

          // 5° Desempate por PLU
          const pluA = a?.plu ? Number(a.plu) : 0;
          const pluB = b?.plu ? Number(b.plu) : 0;
          return pluA - pluB;
        });
      }
      // Si no hay sortType o es 'relevancia' (para categorías normales)
      else if (!sortType || sortType === 'relevancia') {
        resultado.sort((a, b) => {
          const ordenA = typeof a?.orden === 'number' ? a.orden : 0;
          const ordenB = typeof b?.orden === 'number' ? b.orden : 0;

          const groupA = ordenA === 1 ? 0 : 1;
          const groupB = ordenB === 1 ? 0 : 1;
          if (groupA !== groupB) return groupA - groupB;

          const ventasA = typeof a?.cantVentas === 'number' ? a.cantVentas : 0;
          const ventasB = typeof b?.cantVentas === 'number' ? b.cantVentas : 0;

          if (ventasB !== ventasA) return ventasB - ventasA;

          const pluA = a?.plu ? Number(a.plu) : 0;
          const pluB = b?.plu ? Number(b.plu) : 0;
          return pluA - pluB;
        });
      } else {
        switch (sortType) {
          case 'precio-asc':
            resultado.sort((a, b) => getPrecioFinal(a) - getPrecioFinal(b));
            break;
          case 'precio-desc':
            resultado.sort((a, b) => getPrecioFinal(b) - getPrecioFinal(a));
            break;
          case 'alfabetico':
            resultado.sort((a, b) => {
              const nameA = (a.descripcion || '').toLowerCase();
              const nameB = (b.descripcion || '').toLowerCase();
              return nameA.localeCompare(nameB);
            });
            break;
          case 'alfabetico-desc':
            resultado.sort((a, b) => {
              const nameA = (a.descripcion || '').toLowerCase();
              const nameB = (b.descripcion || '').toLowerCase();
              return nameB.localeCompare(nameA);
            });
            break;
          case 'mas-vendido':
            resultado.sort((a, b) => (b.cantVentas || 0) - (a.cantVentas || 0));
            break;
          case 'ofertas': {
            const isPromo = (p) => {
              const tieneOferta = !!p.oferta;
              const tieneCombo = Array.isArray(p.combo) ? p.combo.length > 0 : !!p.combo;
              return tieneOferta || tieneCombo;
            };
            resultado.sort((a, b) => {
              const pa = isPromo(a) ? 1 : 0;
              const pb = isPromo(b) ? 1 : 0;
              if (pb !== pa) return pb - pa;
              return 0;
            });
            break;
          }
          default:
            break;
        }
      }

      return [...resultado, ...faltantes];
    } catch (err) {
      console.error('Error en applySorting:', err);
      return sortedProducts;
    }
  };

  // Inicializar los productos filtrados cuando se cargan los productos desde API
  useEffect(() => {
    if (productosApi && productosApi.length > 0) {
      setFilteredProducts(productosApi);
    }
  }, [productosApi]);

  const displayProducts = useMemo(() => {
    const arr = Array.isArray(filteredProducts) ? filteredProducts : [];
    return applySorting([...arr], sortOption || 'relevancia', isOfertasSearch);
  }, [filteredProducts, sortOption, isOfertasSearch]);

  // Los estados ya estÃ¡n declarados al principio del componente

  return (
    <View style={[styles.container, isSmallWeb && styles.containerMobilePadding]}>
      {/* Barra de navegaciÃ³n superior con fondo rojo */}
      <View style={styles.topNavBar}>
        {/*isSmallScreen ? null :
          <View style={styles.searchTextContainer}>
            {searchText ? (
              // Búsqueda por texto
              <>
                <Text style={styles.searchLabel}>búsqueda:</Text>
                <Text style={styles.searchTermText}>"{searchText}"</Text>
              </>
            ) : isBannerSearch ? (
              // Búsqueda por banner
              <Text style={styles.searchLabel}>búsqueda por banner</Text>
            ) : (departmentName || categoryName || subcategoryName) ? (
              // Navegación por categorías - mostrar breadcrumbs clickeables
              <View style={styles.breadcrumbsContainer}>
                <Text style={styles.breadcrumbLabel}>Busqueda:</Text>
                {renderCategoryPathLinks()}
              </View>
            ) : null}
          </View>
        */}
        <View style={styles.ordenamientoWrapper}>
          <Ordenamiento key={`ord-${uiResetKey}`} onSortChange={handleSortChange} />
        </View>
      </View>

      {/* BotÃ³n de filtros para mÃ³vil justo debajo del header */}
      {isMobile && (
        <TouchableOpacity
          style={styles.filterButtonFullWidth}
          onPress={() => setShowFiltersMobile(!showFiltersMobile)}
        >
          <Text style={styles.filterButtonText}>
            {showFiltersMobile ? 'Ocultar Filtros' : 'Mostrar Filtros'}
          </Text>
        </TouchableOpacity>
      )}

      <View style={[styles.contentContainer, isMobile ? styles.mobileContentContainer : null]}>
        {!isMobile && (
          <View style={styles.filtersPanel}>
            <ScrollView style={styles.filtersScrollView}>
              <Filtros
                key={`fil-${uiResetKey}`}
                productos={productosApi}
                categorias={categorias}
                marcas={marcas}
                onFiltersChanged={handleFiltersChanged}
              />
            </ScrollView>
          </View>
        )}

        <View style={[styles.productsContainer, isMobile ? styles.mobileProductsContainer : null]}>
          {isMobile && showFiltersMobile && (
            <View style={styles.mobileFiltersContainer}>
              <ScrollView style={styles.mobileFiltersScroll}>
                <Filtros
                  key={`film-${uiResetKey}`}
                  productos={productosApi}
                  categorias={categorias}
                  marcas={marcas}
                  onFiltersChanged={handleFiltersChanged}
                  closeFilters={() => setShowFiltersMobile(false)}
                />
              </ScrollView>
            </View>
          )}
          <FlatList
            nativeID="productosFlatList"
            ref={listRef}
            style={styles.productosScroll}
            data={loading ? [] : (displayProducts || [])}
            keyExtractor={(item) => String(item.plu)}
            renderItem={({ item }) => (
              <View style={[
                isMobile ? styles.productItemWrapperMobile : styles.productItemWrapperDesktop,
                { width: `${(100 / columns).toFixed(4)}%`, paddingHorizontal: isMobile ? 4 : 8 }
              ]}>
                <PlaceHolderProducto
                  plu={item.plu}
                  descripcion={item.descripcion}
                  foto={item.foto}
                  faltante={item.faltante === true}
                  fotos={item.fotos}
                  preUnMed={item.preUnMed}
                  precioSinImp={item.precioSinImp}
                  tipo={item.tipo}
                  nombre={item.nombre}
                  precio={item.precio}
                  combo={item.combo}
                  oferta={item.oferta}
                  cuotasSinInt={item.cuotasSinInt || 0}
                  minStock={item.minStock}
                  umedida={item.umedida}
                  cucarda={item.cucarda}
                  stock={item.stock}
                  maxStock={item.maxStock}
                  masInfo={item.masInfo}
                  desCat={item.desCat}
                />
              </View>
            )}
            numColumns={columns}
            key={`cols-${columns}-r${uiResetKey}`}
            {...(columns > 1 && {
              columnWrapperStyle: [
                isMobile ? styles.columnWrapperMobile : styles.columnWrapperDesktop,
                { justifyContent: 'flex-start', paddingHorizontal: 0 }
              ]
            })}
            contentContainerStyle={isMobile ? styles.listContentMobile : styles.listContentDesktop}
            onScroll={handleScroll}
            scrollEventThrottle={16}
            windowSize={7}
            initialNumToRender={12}
            maxToRenderPerBatch={12}
            removeClippedSubviews={true}
            ListEmptyComponent={
              !loading && (
                <View style={styles.noProductsContainer}>
                  <Text style={styles.noProductsTitle}>No se encontraron productos</Text>
                  <Text style={styles.noProductsText}>
                    {displayProducts && displayProducts.length > 0
                      ? 'No hay productos que coincidan con los filtros seleccionados.'
                      : 'No hay productos disponibles en este momento.'}
                  </Text>
                  <TouchableOpacity
                    style={styles.backButton}
                    onPress={() => navigation.navigate('Home')}
                  >
                    <Text style={styles.backButtonText}>Volver al inicio</Text>
                  </TouchableOpacity>
                </View>
              )
            }
          />
        </View>
      </View>

      {/* Botón flotante para volver arriba */}
      <ScrollToTopButton
        onScrollToTop={() => {
          if (listRef.current && typeof listRef.current.scrollToOffset === 'function') {
            listRef.current.scrollToOffset({ offset: 0, animated: true });
          }
        }}
      />

      {/* Modal de "Lo que más comprás" o Login si no está logeado */}
      {showMasComprasModal && !codUsu ? (
        <Login 
          visible={true}
          onClose={() => setShowMasComprasModal(false)}
          navigation={navigation} 
        />
      ) : (
        <Modal
          visible={showMasComprasModal && !!codUsu}
          transparent={true}
          animationType="fade"
          onRequestClose={() => setShowMasComprasModal(false)}
        >
          <View style={styles.modalOverlay}>
            <View style={styles.modalContent}>
              <View style={styles.modalHeader}>
                <MaterialCommunityIcons name="information-outline" size={32} color={colores.rojo} />
                <Text style={styles.modalTitle}>Lo que más comprás</Text>
              </View>
              
              <Text style={styles.modalMessage}>
                En esta sección encontrá los productos que más comprás en tienda física y online
              </Text>
              
              <View style={styles.modalButtonsContainer}>
                <TouchableOpacity 
                  style={styles.modalButton}
                  onPress={() => setShowMasComprasModal(false)}
                >
                  <Text style={styles.modalButtonText}>Entendido</Text>
                </TouchableOpacity>
                
                <TouchableOpacity 
                  style={styles.modalButtonSecondary}
                  onPress={() => {
                    if (Platform.OS === 'web' && typeof window !== 'undefined') {
                      localStorage.setItem('masComprasModalShown', 'true');
                    }
                    setShowMasComprasModal(false);
                  }}
                >
                  <MaterialCommunityIcons name="check" size={18} color={colores.rojo} />
                  <Text style={styles.modalButtonSecondaryText}>No volver a mostrar</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </Modal>
      )}

      {/* Overlay de carga que tapa toda la pantalla */}
      {loading && <LoadingScreen message="Cargando productos..." />}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colores.fondo,
    marginTop: 0,
    paddingHorizontal: layout.contentPadding,
  },
  containerMobilePadding: {
    paddingHorizontal: layout.contentPaddingMobile,
  },
  topBarContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  searchTextContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 15,
    marginLeft: 5,
  },
  ordenamientoWrapper: {
    marginLeft: Platform.OS === 'web' && Dimensions.get('window').width >= 768 ? 240 : 0,
  },
  searchLabel: {
    fontSize: 14,
    marginRight: 5,
    color: colores.textoPrincipal,
  },
  searchTermText: {
    fontSize: 14,
    fontWeight: 'bold',
    color: colores.rojo,
  },
  filterButtonFullWidth: {
    backgroundColor: colores.blanco,
    marginTop: 5,
    paddingVertical: 5,
    width: '50%',
    alignItems: 'center',
    alignSelf: 'center',
    justifyContent: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#ddd',
    borderRadius: 50,
    zIndex: 5,
  },
  filterButtonText: {
    color: colores.rojo,
    fontWeight: 'bold',
    fontSize: 15,
  },
  contentContainer: {
    flex: 1,
    flexDirection: 'row',
  },
  filtersPanel: {
    width: 240,
    height: '100%',
    flexDirection: 'column',
  },
  filtersScrollView: {
    flex: 1,
  },
  productsContainer: {
    flex: 1,
  },
  clearFiltersText: {
    fontSize: 12,
    color: colores.textoSecundario,
    textAlign: 'center',
  },
  productosScroll: {
    flex: 1,
  },
  productosGrid: {
    padding: 15,
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    gap: 15,
  },
  productosGridMobile: {
    paddingHorizontal: 10,
    gap: 8,
    justifyContent: 'space-between',
  },
  // Nuevos estilos para FlatList
  listContentDesktop: {
    paddingTop: 15,
  },
  listContentMobile: {
    paddingBottom: 10,
  },
  columnWrapperDesktop: {
    justifyContent: 'flex-start',
    paddingHorizontal: 0,
    marginBottom: 15,
  },
  columnWrapperMobile: {
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  productItemWrapperDesktop: {
    width: '23%',
    marginBottom: 15,
  },
  productItemWrapperMobile: {
    width: '48%',
    marginBottom: 8,

  },
  mobileContentContainer: {
    flexDirection: 'column',
  },
  mobileProductsContainer: {
    paddingTop: 10,
  },
  mobileFiltersContainer: {
    width: '100%',
    marginBottom: 10,
    marginTop: 5,
    zIndex: 1,
    position: 'relative',
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 10,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }
      : {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 3,
      }
    ),
  },
  mobileFiltersScroll: {
    maxHeight: Platform.OS === 'web' ? '60vh' : 420,
    paddingRight: 6,
  },
  topNavBar: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    padding: 5,
    paddingLeft: 0,
    borderBottomWidth: 1,
    borderBottomColor: colores.rojo,
    zIndex: 10,
    position: 'relative',
    marginTop: Platform.OS === 'web' ? 0 : 40,
  },
  noProductsContainer: {
    flex: 1,
    padding: 20,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 50,
  },
  noProductsTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 15,
    color: colores.textoPrincipal,
  },
  noProductsText: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 30,
    color: colores.textoSecundario,
    maxWidth: 500,
  },
  scrollToTopButton: {
    ...(Platform.OS === 'web'
      ? {
        position: 'fixed',
        bottom: 30,
        left: '50%',
        marginLeft: -25,
        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
      }
      : {
        position: 'absolute',
        bottom: 30,
        left: '50%',
        marginLeft: -25,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.3,
        shadowRadius: 4,
        elevation: 8,
      }
    ),
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: colores.rojo,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999,
  },
  backButton: {
    backgroundColor: colores.rojo,
    paddingVertical: 12,
    paddingHorizontal: 25,
    borderRadius: 8,
  },
  backButtonText: {
    color: colores.blanco,
    fontSize: 16,
    fontWeight: 'bold',
  },

  // Estilos para la navegación por categorías (breadcrumbs)
  breadcrumbsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 5,
   
  },
  breadcrumbLabel: {
    fontSize: 14,
    fontFamily: fuentes.regular,
    color: colores.textoPrincipal,
    marginRight: 5,
  },
  categoryPathContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'nowrap',
  },
  categoryText: {
    fontSize: 14,
    fontFamily: fuentes.regular,
    color: colores.rojo,
  },
  categoryTextBold: {
    fontSize: 14,
    fontFamily: fuentes.bold,
    color: colores.textoPrincipal,
  },
  categoryLinkText: {
    fontSize: 14,
    fontFamily: fuentes.regular,
  },
  categoryPathSeparator: {
    fontSize: 14,
    fontFamily: fuentes.bold,
    color: colores.textoPrincipal,
    marginHorizontal: 2,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 20,
  },
  modalContent: {
    backgroundColor: colores.blanco,
    borderRadius: 12,
    padding: 24,
    width: '100%',
    maxWidth: 400,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  modalTitle: {
    fontFamily: fuentes.bold,
    fontSize: 18,
    color: colores.rojo,
    marginLeft: 12,
  },
  modalMessage: {
    fontFamily: fuentes.regular,
    fontSize: 14,
    color: colores.texto,
    lineHeight: 22,
    marginBottom: 24,
  },
  modalButtonsContainer: {
    gap: 12,
  },
  modalButton: {
    backgroundColor: colores.rojo,
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 6,
    alignItems: 'center',
  },
  modalButtonText: {
    fontFamily: fuentes.bold,
    fontSize: 14,
    color: colores.blanco,
  },
  modalButtonSecondary: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 6,
    borderWidth: 2,
    borderColor: colores.rojo,
    gap: 8,
  },
  modalButtonSecondaryText: {
    fontFamily: fuentes.regular,
    fontSize: 14,
    color: colores.rojo,
  },
});

export default Productos;
